import React, { useEffect, useState } from "react";
import TableHeader from "../../common/MagicPageHeader";
import Header from "../../common/MagicTableHeader";
import ImageComponent from "../../mui/Image";
import DownloadToXlsxFile from "../../../helper/DownloadToXlsxFile";
import GridTable, { Column } from "../../common/GridTable";
import { useFetchAttendanceSummaryReportsQuery } from "../../../store/apis/reportsApis";



interface TabCategory {
  id: number;
  name: string;
  code:string;
}

interface AttendanceTableProps {
  selectedTab: TabCategory | null;
  magicPageHeaderBtn: {
    text: string;
    variant: string;
    startIcon: React.ReactNode;
    onClick: () => void;
    sx: Record<string, string>;
  }[];
  magicTableHeaderBtn: any[];
  params:any;
  isInvalidSearch: boolean;
  onPaginationChange: (pageNo: number) => void;
  onSearch: (searchTerm: string) => void;
}
const columns: Column[] = [
  {
    field: "centre",
    headerText: "",
  },
  {
    field: "funder",
    headerText: "",
  },
  {
    field: "batchId",
    headerText: "",
  },
  {
    field: "batchStartDate",
    headerText: "",
  },
  {
    field: "batchEndDate",
    headerText: "",
  },
  {
    field: "lessThanTwentyPercent",
    headerText: "<19%",
  },
  {
    field: "twentyToFiftyPercent",
    headerText: ">=20% - 50%",
  },
  {
    field: "fiftyToSeventyFivePercent",
    headerText: ">=50% - 75%",
  },
  {
    field: "seventyFiveToNintyPercent",
    headerText: ">=75% - 90%",
  },
  {
    field: "greaterThanNintyPercent",
    headerText: ">=90%",
  },
];


const headers = [
  {
    id: 'centre',
    colSpan: 1,
    label: "Centre",
    rowSpan: 3,
    align: 'center' as const,
  },
  {
    id: 'funder',
    colSpan: 1,
    label: "Funder",
    rowSpan: 3,
    align: 'center' as const,
  },
  {
    id: 'batchId',
    colSpan: 1,
    label: "Batch ID",
    rowSpan: 3,
    align: 'center' as const,
  },
  {
    id: 'batchStartDate',
    colSpan: 1,
    label: "Batch Start Date",
    rowSpan: 3,
    align: 'center' as const,
  },
  {
    id: 'batchEndDate',
    colSpan: 1,
    label: "Batch End Date",
    rowSpan: 3,
    align: 'center' as const,
  },
  {
    id: 'totalEnrollment',
    colSpan: 5,
    label: "Total number of YP's enrolled in the batch",
    align: 'center' as const,
  },
];

const AttendanceTableSummaryReport: React.FC<AttendanceTableProps> = ({
  selectedTab,
  magicTableHeaderBtn,
  params,
  isInvalidSearch,
  onPaginationChange,
  onSearch,
}) => {
  const [downloadParams, setDownloadParams] = useState<any>(null);
  const [downloadTrigger, setDownloadTrigger] = useState(false);
  const {
    data: fetchedReports = {
      reports: [],
      totalReports: 0,
    },
    isLoading,
  } = useFetchAttendanceSummaryReportsQuery(params);
  const { data: downloadData, isLoading: downloadLoading } =
    useFetchAttendanceSummaryReportsQuery(downloadParams, {
      skip: !downloadParams,
    });
    const handleDownload = () => {
      const downloadPayload = {
        ...params,
        pageNo: "1",
        pageSize: fetchedReports?.totalReports || "10",
      };
      setDownloadParams(downloadPayload);
      setDownloadTrigger(true); 
    };

  useEffect(() => {
    if (downloadData && downloadTrigger) {
      const sheetData = [
        [
          "Centre",
          "Funder",
          "Batch ID",
          "Batch Start Date",
          "Batch End Date",
          "<19%",
          ">=20% - 50%",
          ">=50% - 75%",
          ">=75% - 90%",
          ">=90%",
        ],
        ...downloadData?.reports.map((report: any) => [
          report.centre || "-",
          report.funder || "-",
          report.batchId || "-",
          report.batchStartDate || "-",
          report.batchEndDate || "-",
          report.lessThanTwentyPercent || "-",
          report.twentyToFiftyPercent || "-",
          report.fiftyToSeventyFivePercent || "-",
          report.seventyFiveToNintyPercent || "-",
          report.greaterThanNintyPercent || "-",
        ]),
      ];

      DownloadToXlsxFile({
        fileNamePrifix: "attendance-summary-report",
        sheetData,
      });
      setDownloadTrigger(false);  
    }
    }, [downloadData, downloadTrigger]);  
  
  const magicPageHeaderBtn = [
    {
      text: "Download",
      variant: "outlined",
      startIcon: (
        <ImageComponent
          src="/assets/images/login/exportIcon.svg"
          alt="ExportIcon"
        />
      ),
      onClick: handleDownload,
      loading: downloadLoading,
      sx: {
        background: "white",
        border: "1px solid #D0D5DD",
        borderRadius: "8px",
        color: "black",
      },
    },
  ];

  const totalPages = Math.max(
    1,
    Math.ceil((fetchedReports?.totalReports || 0) / 10)
  );

  return (
    <>
      <TableHeader
        title={`${selectedTab?.name || "Attendance"} Report`}
        subTitle="Here you can view the report with all the details."
        buttons={magicPageHeaderBtn}
        chipLabel={`${fetchedReports?.totalReports || 0} entries`}
      />
      <Header
        buttons={magicTableHeaderBtn}
        onSearchfieldChange={(value) => onSearch(String(value))}
        isInvalidSearch={isInvalidSearch}
      />
      <GridTable
        columns={columns}
        rows={fetchedReports?.reports}
        headers={headers}
        loading={isLoading}
        handlePageChange={(event, pageNo) => onPaginationChange(pageNo)}
        count={totalPages}
        rowsPerPageOptions={[10, 25]}
        initialRowsPerPage={10}
        tableHeight={500}
        currentPage={parseInt(params?.pageNo) || 1}
      />
    </>
  );
};

export default AttendanceTableSummaryReport;